import { useState, useCallback, useEffect, useRef } from 'react';
import type { Place, MarkerState } from '../types/Place';
import type { AnimationState } from './useAnimation';

export type { MarkerState };

interface UseMarkerStateResult {
  visitedPlaces: Set<string>;
  highlightedPlace: string | null;
  getMarkerState: (placeId: string) => MarkerState;
  markVisited: (placeId: string) => void;
  markPreviousAsViewed: (targetPlaceId: string) => void;
  getNextUnvisitedPlace: () => string | null;
  handlePopupClose: () => string | null;
  setHighlightedPlace: (placeId: string | null) => void;
  clearHighlight: () => void;
  reset: () => void;
}

export function useMarkerState(
  places: Place[],
  animationState: AnimationState,
  selectedPlace: string | null,
  filteredPlaces?: Place[]
): UseMarkerStateResult {
  // Use filteredPlaces for navigation if provided, otherwise use all places
  const navigablePlaces = filteredPlaces ?? places;
  const [visitedPlaces, setVisitedPlaces] = useState<Set<string>>(new Set());
  const [highlightedPlace, setHighlightedPlaceInternal] = useState<string | null>(null);

  // Track if we've already set initial highlight after animation
  const initialHighlightSetRef = useRef(false);

  // Calculate next unvisited place in chronological order (within navigable/filtered places)
  const getNextUnvisitedPlace = useCallback((): string | null => {
    for (const place of navigablePlaces) {
      if (!visitedPlaces.has(place.id)) {
        return place.id;
      }
    }
    return null;
  }, [navigablePlaces, visitedPlaces]);

  // Mark a place as visited
  const markVisited = useCallback((placeId: string) => {
    setVisitedPlaces(prev => new Set([...prev, placeId]));
  }, []);

  // Mark all places before target as viewed (for Journal→Map navigation, AC: #3)
  const markPreviousAsViewed = useCallback((targetPlaceId: string) => {
    const targetIndex = places.findIndex(p => p.id === targetPlaceId);
    if (targetIndex <= 0) return; // No previous places to mark

    setVisitedPlaces(prev => {
      const newVisited = new Set(prev);
      for (let i = 0; i < targetIndex; i++) {
        newVisited.add(places[i].id);
      }
      return newVisited;
    });
  }, [places]);

  // Set highlighted place
  const setHighlightedPlace = useCallback((placeId: string | null) => {
    setHighlightedPlaceInternal(placeId);
  }, []);

  // Clear highlight
  const clearHighlight = useCallback(() => {
    setHighlightedPlaceInternal(null);
  }, []);

  // Handle popup close - marks current place visited, highlights next, returns next ID for centering
  // Only navigates through filtered/navigable places when a layer filter is active
  const handlePopupClose = useCallback((): string | null => {
    if (!selectedPlace) return null;

    // Compute the new visited set synchronously
    const newVisited = new Set([...visitedPlaces, selectedPlace]);

    // Find current place index in navigable places
    const currentIndex = navigablePlaces.findIndex(p => p.id === selectedPlace);

    // Calculate next unvisited starting AFTER current place, then wrap around
    // Only search within navigable places (respects layer filter)
    let nextPlace: string | null = null;
    const totalPlaces = navigablePlaces.length;

    // If current place is in navigable places, search from there
    if (currentIndex !== -1) {
      for (let i = 1; i <= totalPlaces; i++) {
        const checkIndex = (currentIndex + i) % totalPlaces;
        const place = navigablePlaces[checkIndex];
        if (!newVisited.has(place.id)) {
          nextPlace = place.id;
          break;
        }
      }
    } else {
      // Current place not in navigable places (filter changed), find first unvisited
      for (const place of navigablePlaces) {
        if (!newVisited.has(place.id)) {
          nextPlace = place.id;
          break;
        }
      }
    }

    // Update states
    setVisitedPlaces(newVisited);
    setHighlightedPlaceInternal(nextPlace);

    // Return next place ID for map centering
    return nextPlace;
  }, [selectedPlace, navigablePlaces, visitedPlaces]);

  // Get marker state for a given place
  const getMarkerState = useCallback(
    (placeId: string): MarkerState => {
      // Priority: active > visited > highlighted > default
      if (selectedPlace === placeId) {
        return 'active';
      }
      if (visitedPlaces.has(placeId)) {
        return 'visited';
      }
      if (highlightedPlace === placeId) {
        return 'highlighted';
      }
      return 'default';
    },
    [selectedPlace, visitedPlaces, highlightedPlace]
  );

  // Reset all state
  const reset = useCallback(() => {
    setVisitedPlaces(new Set());
    setHighlightedPlaceInternal(null);
    initialHighlightSetRef.current = false;
  }, []);

  // Set initial highlight when animation completes
  useEffect(() => {
    if (animationState === 'complete' && !initialHighlightSetRef.current) {
      initialHighlightSetRef.current = true;
      const nextPlace = getNextUnvisitedPlace();
      setHighlightedPlaceInternal(nextPlace);
    }
  }, [animationState, getNextUnvisitedPlace]);

  return {
    visitedPlaces,
    highlightedPlace,
    getMarkerState,
    markVisited,
    markPreviousAsViewed,
    getNextUnvisitedPlace,
    handlePopupClose,
    setHighlightedPlace,
    clearHighlight,
    reset,
  };
}
